import { Component, Inject, OnInit, Optional, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ClientAdminService } from '../client/client-admin.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';

@Component({
    selector: 'gtw-workflow-add-modal',
    templateUrl: './workflow-add-modal.component.html',
    styleUrls: ['./workflow-add-modal.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class WorkflowAddModalComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  title = '';
  origin: 'Category' | 'Step' = 'Category';
  minimise = false;

  name = '';
  description = '';
  selectedIsActive = '';
  priority = '';
  selectedCategory = '';
  availableCategories: any[] = [];

  dirty: Record<string, boolean> = {};
  saving = false;

  private defaultTreeData: any[] = [];
  private clientTreeData: any[] = [];
  private clientKey = '';
  private wfstKey = '';
  private taxYear = '';
  private scenario = '';

  constructor(
    @Optional() public dialogRef: MatDialogRef<WorkflowAddModalComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private clientAdminService: ClientAdminService,
    private snackbar: GtwSnackbarService,
  ) {}

  ngOnInit(): void {
    this.origin = this.data?.origin || 'Category';
    this.title = `Add ${this.origin}`;
    this.defaultTreeData = this.data?.defaultTreeData || [];
    this.clientTreeData = this.data?.clientTreeData || [];
    this.clientKey = this.data?.clientKey ?? '';
    this.wfstKey = this.data?.wfstKey ?? '';
    this.taxYear = this.data?.taxYear || '';
    this.scenario = this.data?.scenario || '';

    if (this.origin === 'Step') {
      this.loadCategoryItems();
    }
  }

  private loadCategoryItems(): void {
    this.availableCategories = this.clientTreeData.filter(
      n => n.is_selected !== 0 && n.nav_level === '1'
    );
  }

  isNameMissing(): boolean {
    return this.dirty['name'] && !this.name?.trim();
  }

  isSelectedIsActiveMissing(): boolean {
    return this.dirty['selectedIsActive'] && !this.selectedIsActive;
  }

  isSelectedCategoryMissing(): boolean {
    if (this.origin === 'Category') return false;
    return this.dirty['selectedCategory'] && !this.selectedCategory;
  }

  isPriorityMissing(): boolean {
    if (this.origin === 'Category') return false;
    return this.dirty['priority'] && !this.priority?.trim();
  }

  save(): void {
    const fields = this.origin === 'Category'
      ? ['name', 'selectedIsActive']
      : ['name', 'selectedIsActive', 'selectedCategory', 'priority'];
    fields.forEach(f => this.dirty[f] = true);

    if (this.isNameMissing() || this.isSelectedIsActiveMissing() ||
        this.isSelectedCategoryMissing() || this.isPriorityMissing()) {
      return;
    }

    this.saving = true;
    const params: Record<string, any> = {
      clientKey: this.clientKey,
      name: this.name,
      description: this.description || '',
      selectedCategory: this.selectedCategory || '',
      priority: this.priority || '',
      templatePath: '',
      controllerPath: '',
      acitivityKey: '',
      screenKey: '',
      selectedModalSize: '',
      parentCssClass: '',
      selectedActionType: '',
      selectedIsActive: this.selectedIsActive,
      filter_json: '',
      wfstKey: this.wfstKey,
      taxYear: this.taxYear,
      scenario: this.scenario,
    };

    this.clientAdminService.addWorkflow(params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (resp: any) => {
        this.saving = false;
        const data = resp?.data ?? resp;
        if (data?.result?.value > 0) {
          this.snackbar.openSnackBar(data.result.message || 'Saved successfully.', 'gtw-snackbar--green');
          this.dialogRef.close({ saved: true });
        } else {
          this.snackbar.openSnackBar(data?.result?.message || 'Error saving.', 'gtw-snackbar--red');
        }
      },
      error: () => {
        this.saving = false;
        this.snackbar.openSnackBar('Error saving workflow.', 'gtw-snackbar--red');
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
